document.documentElement.classList.add('nimbli-js');

(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initReveals = () => {
    const items = document.querySelectorAll('[data-nimbli-reveal]');
    if (!items.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('nimbli-is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('nimbli-is-visible');
          currentObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    items.forEach((item) => observer.observe(item));
  };

  const initProductsMotion = () => {
    const section = document.querySelector('[data-nimbli-products-section]');
    if (!section) return;

    section.classList.add('nimbli-products-motion');

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      section.classList.add('nimbli-products-animate');
      return;
    }

    const revealObserver = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          section.classList.add('nimbli-products-animate');
          currentObserver.unobserve(entry.target);
          currentObserver.disconnect();
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    revealObserver.observe(section);

    const heroImage = section.querySelector('[data-nimbli-products-hero-media] img');
    const lowPowerDevice =
      (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) ||
      (navigator.deviceMemory && navigator.deviceMemory <= 4);
    const desktop = window.matchMedia('(min-width: 1100px)');

    if (!heroImage || lowPowerDevice || !desktop.matches) return;

    let depthActive = false;
    let depthFrame = 0;

    const updateDepth = () => {
      depthFrame = 0;
      const bounds = section.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const sectionCenter = bounds.top + bounds.height / 2;
      const progress = (viewportCenter - sectionCenter) / Math.max(window.innerHeight, bounds.height);
      const offset = Math.max(-16, Math.min(16, progress * 32));
      heroImage.style.setProperty('--nimbli-products-parallax', `${offset.toFixed(2)}px`);
    };

    const requestDepthUpdate = () => {
      if (depthFrame) return;
      depthFrame = window.requestAnimationFrame(updateDepth);
    };

    const startDepth = () => {
      if (depthActive) return;
      depthActive = true;
      window.addEventListener('scroll', requestDepthUpdate, { passive: true });
      requestDepthUpdate();
    };

    const stopDepth = () => {
      if (!depthActive) return;
      depthActive = false;
      window.removeEventListener('scroll', requestDepthUpdate);
      if (depthFrame) window.cancelAnimationFrame(depthFrame);
      depthFrame = 0;
    };

    const depthObserver = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          startDepth();
          return;
        }

        stopDepth();
        if (entry.boundingClientRect.bottom < 0) currentObserver.disconnect();
      });
    });

    depthObserver.observe(section);
  };

  const initHeroVideo = () => {
    const media = document.querySelector('[data-nimbli-hero-media]');
    const video = media ? media.querySelector('[data-nimbli-hero-video]') : null;
    if (!media || !video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.autoplay = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    video.setAttribute('autoplay', '');
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('disableremoteplayback', '');
    video.setAttribute('preload', 'auto');
    video.setAttribute('aria-hidden', 'true');
    video.setAttribute('tabindex', '-1');

    const markReady = () => {
      media.classList.remove('nimbli-home-hero__media--error');
      media.classList.add('nimbli-home-hero__media--ready');
    };

    const markError = () => {
      media.classList.remove('nimbli-home-hero__media--ready');
      media.classList.add('nimbli-home-hero__media--error');
    };

    const play = () => {
      media.classList.remove('nimbli-home-hero__media--reduced-motion');
      video.autoplay = true;
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('autoplay', '');
      const playPromise = video.play();
      if (playPromise) playPromise.catch(() => {});
    };

    const stopForReducedMotion = () => {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.pause();
      media.classList.remove('nimbli-home-hero__media--error');
      media.classList.add('nimbli-home-hero__media--reduced-motion');

      if (video.readyState >= 1 && video.currentTime !== 0) {
        try {
          video.currentTime = 0;
        } catch {}
      }
    };

    const syncMotionPreference = () => {
      if (reducedMotion.matches) {
        stopForReducedMotion();
        return;
      }

      play();
    };

    video.addEventListener('loadeddata', markReady, { once: true });
    video.addEventListener('playing', markReady);
    video.addEventListener('error', markError, { once: true });
    video.addEventListener('canplay', syncMotionPreference, { once: true });

    if (video.readyState >= 2) {
      markReady();
    }

    syncMotionPreference();

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && video.paused && !reducedMotion.matches) play();
    });

    reducedMotion.addEventListener('change', syncMotionPreference);
  };

  const initVideoControls = () => {
    document.querySelectorAll('[data-nimbli-video-toggle]').forEach((button) => {
      const container = button.closest('[data-nimbli-video-container]');
      const video = container ? container.querySelector('video') : null;
      if (!video) return;

      const updateButton = () => {
        const paused = video.paused;
        button.setAttribute('aria-pressed', paused ? 'true' : 'false');
        button.setAttribute(
          'aria-label',
          paused ? 'Lire la démonstration vidéo' : 'Mettre la démonstration vidéo en pause'
        );
        button.textContent = paused ? 'Lire' : 'Pause';
      };

      if (reducedMotion.matches) video.pause();
      updateButton();

      button.addEventListener('click', () => {
        if (video.paused) {
          const playPromise = video.play();
          if (playPromise) playPromise.catch(() => updateButton());
        } else {
          video.pause();
        }
        updateButton();
      });

      video.addEventListener('play', updateButton);
      video.addEventListener('pause', updateButton);
    });
  };

  const initComparisons = () => {
    document.querySelectorAll('[data-nimbli-comparison]').forEach((comparison) => {
      const range = comparison.querySelector('[data-nimbli-comparison-range]');
      const output = comparison.querySelector('[data-nimbli-comparison-output]');
      if (!range) return;

      const update = () => {
        const value = Number(range.value);
        comparison.style.setProperty('--nimbli-compare-position', `${value}%`);
        range.setAttribute('aria-valuetext', `${value} % de l’image avant`);
        if (output) output.textContent = `${value} %`;
      };

      range.addEventListener('input', update, { passive: true });
      update();
    });
  };

  const initNewsletterLocks = () => {
    document.querySelectorAll('[data-nimbli-newsletter-form]').forEach((form) => {
      form.addEventListener('submit', () => {
        const submit = form.querySelector('[type="submit"]');
        if (!submit) return;
        submit.disabled = true;
        submit.textContent = 'Envoi…';
      });
    });
  };

  const initCollectionDrawerAccessibility = () => {
    document.querySelectorAll('[aria-controls^="NimbliCollectionDrawer-"]').forEach((trigger) => {
      const drawer = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!drawer) return;

      const closeButton = drawer.querySelector('[data-close="FilterSidebar"]');
      const focusableElements = () =>
        Array.from(
          drawer.querySelectorAll(
            'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter(
          (element) =>
            !element.hidden &&
            element.getAttribute('aria-hidden') !== 'true' &&
            element.getClientRects().length > 0 &&
            window.getComputedStyle(element).visibility !== 'hidden'
        );
      const firstFocusable = () => focusableElements()[0];
      let wasOpen = drawer.classList.contains('act');

      const sync = () => {
        const isOpen = drawer.classList.contains('act');
        trigger.setAttribute('aria-expanded', String(isOpen));

        if (isOpen) {
          drawer.removeAttribute('inert');
          drawer.setAttribute('aria-hidden', 'false');
        } else {
          if (wasOpen && drawer.contains(document.activeElement)) trigger.focus();
          drawer.setAttribute('aria-hidden', 'true');
          drawer.setAttribute('inert', '');
        }

        if (isOpen && !wasOpen) {
          window.requestAnimationFrame(() => firstFocusable()?.focus());
        }

        wasOpen = isOpen;
      };

      const observer = new MutationObserver(sync);
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });

      trigger.addEventListener('click', () => window.requestAnimationFrame(sync));
      closeButton?.addEventListener('click', () => window.requestAnimationFrame(sync));
      drawer.addEventListener('keydown', (event) => {
        if (!drawer.classList.contains('act')) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          closeButton?.click();
          trigger.focus();
          return;
        }

        if (event.key !== 'Tab') return;
        const focusable = focusableElements();
        if (!focusable.length) {
          event.preventDefault();
          drawer.focus();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      sync();
    });
  };

  const initCollectionSortAccessibility = () => {
    document.querySelectorAll('[aria-controls^="NimbliCollectionSort-"]').forEach((trigger) => {
      const menu = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!menu) return;

      trigger.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || trigger.getAttribute('aria-expanded') !== 'true') return;
        event.preventDefault();
        trigger.click();
        trigger.focus();
      });

      menu.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        if (trigger.getAttribute('aria-expanded') === 'true') trigger.click();
        trigger.focus();
      });
    });
  };

  const init = () => {
    initProductsMotion();
    initReveals();
    initHeroVideo();
    initVideoControls();
    initComparisons();
    initNewsletterLocks();
    initCollectionDrawerAccessibility();
    initCollectionSortAccessibility();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
